import * as React from "react";
import {
  Button,
  Box,
  Paper,
  Typography,
  Snackbar,
  Link,
  CircularProgress,
} from "@mui/material";

import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  confirmSignUp,
  fetchUserAttributes,
  resendSignUpCode,
  signIn,
} from "aws-amplify/auth";

import { FormFieldTextField } from "../shared/components/FormFieldTextField";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { login } from "components/Slices/authSlice";
import { useCreateUserMutation } from "components/Slices/usersApiSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { OTPInput } from "./components/OTPInput";

interface RegisterConfirmValues {
  email: string;
  code: string;
}

export const RegisterConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const pendingEmail = state?.email || localStorage.getItem("pendingEmail");
  const tempPassword = state?.tempPassword;
  const dispatch = useDispatch();
  const [createUser] = useCreateUserMutation();

  const { control, handleSubmit, watch, setValue } =
    useForm<RegisterConfirmValues>({
      mode: "all",
      defaultValues: {
        email: pendingEmail,
        code: "",
      },
    });

  const watchedEmail = watch("email");
  const watchedCode = watch("code");

  const [, setResendStatus] = useState<string | null>(null);
  // 30s, not 120: long enough to stop code-spam, short enough that someone
  // whose email is slow isn't stuck staring at a 2-minute timer.
  const RESEND_COOLDOWN_S = 30;
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const registerSubmit = async (data: RegisterConfirmValues) => {
    if (!data.email || !data.code) {
      setMessage(t("auth.confirm.missingFields"));
      return;
    }
    setLoading(true);

    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: data.email,
        confirmationCode: data.code,
      });
      if (isSignUpComplete) {
        localStorage.removeItem("pendingEmail");
        const signInAttempt = await signIn({
          username: data.email,
          password: tempPassword,
        });
        if (signInAttempt.isSignedIn) {
          const userAws = await fetchUserAttributes();
          dispatch(
            login({
              id: userAws.sub || "",
              email: userAws.email || "",
              firstName: userAws.given_name || "",
              lastName: userAws.family_name || "",
              displayImg: userAws.profile || "",
            }),
          );

          // Repair pass for a signup whose backend-create failed (the Register
          // page stashes the profile instead of dead-ending). The endpoint is
          // idempotent per cognitoId, and if this also fails authMiddleware
          // still creates the row from the token on the first request — this
          // retry mainly preserves `country`, which the token doesn't carry.
          const stashed = localStorage.getItem("pendingSignupProfile");
          if (stashed) {
            try {
              const profile = JSON.parse(stashed);
              await createUser({
                ...profile,
                cognitoId: userAws.sub,
                email: userAws.email || profile.email,
              }).unwrap();
            } catch (err) {
              console.error("Signup repair createUser failed:", err);
            }
            localStorage.removeItem("pendingSignupProfile");
          }

          // Land new users on the onboarding welcome page — it orients them and
          // offers (skippable) verification. Progressive onboarding still holds:
          // every step is optional until their first payout.
          navigate("/onboarding");
          return;
        } else {
          navigate("/login");
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage(t("auth.confirm.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!watchedEmail) return;
    try {
      await resendSignUpCode({ username: watchedEmail });
      setResendStatus("Confirmation code resent!");
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err: any) {
      setResendStatus("Failed to resend code");
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
        px: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          p: { xs: 2.5, sm: 3 },
          width: "100%",
          maxWidth: 400,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: "1.65rem",
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "-0.01em",
            mb: 0.5,
          }}
        >
          {t("auth.confirm.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
          {t("auth.confirm.subtitlePrefix")}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            {watchedEmail}
          </Typography>
          .
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit(registerSubmit)}
          noValidate
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <FormFieldTextField
            control={control}
            rules={{ required: true }}
            id="email"
            label={t("auth.confirm.emailLabel")}
            name="email"
            InputLabelProps={{ shrink: true }}
          />

          <Box>
            <Controller
              name="code"
              control={control}
              rules={{ required: true, minLength: 6, maxLength: 6 }}
              render={({ field, fieldState }) => (
                <OTPInput
                  length={6}
                  value={field.value || ""}
                  onChange={(value) => field.onChange(value)}
                  onComplete={(value) => {
                    field.onChange(value);
                    handleSubmit(registerSubmit)();
                  }}
                  disabled={loading}
                  error={!!fieldState.error}
                />
              )}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={watchedCode?.length !== 6 || loading}
            sx={{ py: 1.25, fontSize: "0.95rem", fontWeight: 600 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("auth.confirm.confirm")
            )}
          </Button>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              variant="text"
              onClick={handleResendCode}
              disabled={cooldown > 0}
              size="small"
              sx={{ fontSize: "0.8rem", color: "text.secondary" }}
            >
              {cooldown > 0
                ? t("auth.confirm.resendIn", { time: formatTime(cooldown) })
                : t("auth.confirm.resendNow")}
            </Button>
            <Link
              component={RouterLink}
              to="/register"
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
                textDecoration: "none",
                "&:hover": { color: "primary.main" },
              }}
            >
              {t("auth.confirm.backToSignUp")}
            </Link>
          </Box>
        </Box>

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={message !== null}
          autoHideDuration={5000}
          message={message}
          onClose={() => setMessage(null)}
          ContentProps={{
            sx: {
              borderRadius: "5px",
              color: "white",
              bgcolor: "error.main",
              fontWeight: "bold",
              fontSize: "1rem",
            },
          }}
        />
      </Paper>
    </Box>
  );
};
export default RegisterConfirm;
